import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as common from '../../../../../common'
import type * as currentRendering from '../../../current-rendering'
import * as enums from '../enums'

export const DOC = gql`
  ${common.gql.flexibleIdentifier.fragments.DOC}
  fragment FragmentMosaicWidgetReference on MosaicWidgetReference {
    identifier {
      ...FragmentFlexibleIdentifier
    }
    __typename
  }
  fragment FragmentMosaicPageLayoutListElementReference on MosaicPageLayoutListElementReference {
    index
    __typename
  }
  fragment FragmentMosaicLayoutElementReference on MosaicLayoutElementReference {
    ...FragmentMosaicWidgetReference
    ...FragmentMosaicPageLayoutListElementReference
  }
  fragment FragmentMosaicColumnElement on MosaicColumnElement {
    height
    ref {
      ...FragmentMosaicLayoutElementReference
    }
  }
  fragment FragmentMosaicRowElement on MosaicRowElement {
    span
    ref {
      ...FragmentMosaicLayoutElementReference
    }
  }
  fragment FragmentMosaicPageLayout on MosaicPageLayout {
    root {
      elements {
        ...FragmentMosaicColumnElement
      }
    }
    columns {
      elements {
        ...FragmentMosaicColumnElement
      }
    }
    rows {
      elements {
        ...FragmentMosaicRowElement
      }
    }
  }
  fragment FragmentMosaicPageUnsegmentedContent on MosaicPageUnsegmentedContent {
    layout {
      ...FragmentMosaicPageLayout
    }
    __typename
  }
  fragment FragmentMosaicPageSegment on MosaicPageSegment {
    alias
    name
    hasQuery
    layout {
      ...FragmentMosaicPageLayout
    }
    __typename
  }
  fragment FragmentMosaicPageSegmentedContent on MosaicPageSegmentedContent {
    navigator
    segments {
      ...FragmentMosaicPageSegment
    }
    __typename
  }
  fragment FragmentMosaicPageContent on MosaicPageContent {
    ...FragmentMosaicPageUnsegmentedContent
    ...FragmentMosaicPageSegmentedContent
  }
`

// TODO remove type asserters, check with Sander how to better write this
// transformer
const LAYOUT_TRANSFORMER = graphql.transformers.OUT.nullable(
  graphql.transformers.OUT.custom<
    graphql.schema.FragmentMosaicPageLayoutFragment,
    ents.core.mosaic.Page.Column
  >(layout => {
    function gqlToPageColumnElement(
      element: graphql.schema.FragmentMosaicColumnElementFragment | null,
    ): ents.core.mosaic.Page.Column.Element {
      TypeAsserter.assertIsNotNullish(element?.ref)
      TypeAsserter.assertIsNotNullish(element.height)
      if (element.ref.__typename === 'MosaicWidgetReference') {
        const widget =
          common.gql.flexibleIdentifier.fragments.TRANSFORMER.getEntityFrom(
            element.ref.identifier,
          )
        TypeAsserter.assertIsNotNullish(widget)
        return {
          content: { widget: { widget } },
          height: element.height,
        }
      } else {
        TypeAsserter.assertIsNotNullish(element.ref.index)
        TypeAsserter.assertIsNotNullish(layout.rows)
        return {
          content: {
            row: {
              elements:
                layout.rows[element.ref.index]?.elements?.map(el =>
                  gqlToPageRowElement(el),
                ) ?? [],
            },
          },
          height: element.height,
        }
      }
    }

    function gqlToPageRowElement(
      element: graphql.schema.FragmentMosaicRowElementFragment | null,
    ): ents.core.mosaic.Page.Row.Element {
      TypeAsserter.assertIsNotNullish(element?.ref)
      TypeAsserter.assertIsNotNullish(element.span)
      if (element.ref.__typename === 'MosaicWidgetReference') {
        const widget =
          common.gql.flexibleIdentifier.fragments.TRANSFORMER.getEntityFrom(
            element.ref.identifier,
          )
        TypeAsserter.assertIsNotNullish(widget)
        return {
          content: {
            widget: { widget },
          },
          span: element.span,
        }
      } else {
        TypeAsserter.assertIsNotNullish(element.ref.index)
        TypeAsserter.assertIsNotNullish(layout.columns)
        return {
          content: {
            column: {
              elements:
                layout.columns[element.ref.index]?.elements?.map(el =>
                  gqlToPageColumnElement(el),
                ) ?? [],
            },
          },
          span: element.span,
        }
      }
    }

    return {
      elements:
        layout.root?.elements?.map(element =>
          gqlToPageColumnElement(element),
        ) ?? [],
    }
  }),
)

export const TRANSFORMER = graphql.transformers.OUT.discriminateOn<
  graphql.schema.FragmentMosaicPageContentFragment,
  currentRendering.models.page.Page.Content.ForRequest,
  '__typename'
>('__typename', {
  MosaicPageSegmentedContent: graphql.transformers.OUT.object({
    navigator: enums.segmentNavigator.TRANSFORMER,
    // TODO: make discriminateOn work correctly with 'MutuallyExclusive'
    root: () => undefined,
    segments: graphql.transformers.OUT.list(
      graphql.transformers.OUT.object<
        graphql.schema.FragmentMosaicPageSegmentFragment,
        currentRendering.models.segment.Segment
      >({
        alias: graphql.transformers.OUT.validate(
          transformers.PASS_THROUGH,
          TypeAsserter.assertIsNotNullish,
        ),
        hasQuery: transformers.PASS_THROUGH,
        name: graphql.transformers.OUT.validate(
          transformers.PASS_THROUGH,
          TypeAsserter.assertIsNotNullish,
        ),
        root: {
          usingFullInput: graphql.transformers.OUT.validate(
            graphql.transformers.OUT.select<
              graphql.schema.FragmentMosaicPageSegmentFragment | Nullish,
              ents.core.mosaic.Page.Column | undefined,
              'layout'
            >('layout', LAYOUT_TRANSFORMER),
            TypeAsserter.assertIsNotNullish,
            { warnOnly: true },
          ),
        },
      }),
      { onNullish: 'remove' },
    ),
  }),
  MosaicPageUnsegmentedContent: graphql.transformers.OUT.select(
    'layout',
    graphql.transformers.OUT.nest(
      'root',
      graphql.transformers.OUT.validate(
        LAYOUT_TRANSFORMER,
        TypeAsserter.assertIsNotNullish,
        { warnOnly: true },
      ),
    ),
  ),
})
